// src/components/admin/CampaignCompositePreview.tsx
import React, { useMemo } from 'react';
import { Player } from '@remotion/player';
import { AbsoluteFill } from 'remotion';
import { POST_LAYOUTS } from '@/lib/layouts/registry';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import type { WatermarkConfig } from '@/lib/campaign-watermark-types';
import { getAnchorStyle } from '@/lib/watermark-anchor-style';
import type { SubtitleAlignItems } from '@/lib/layouts/enforcedSubtitlePosition';
import { subtitleVerticalTransformPercent } from '@/lib/layouts/subtitleVerticalTransform';

const DESIGN_WIDTH = 1080;
const DESIGN_HEIGHT = 1920;

export type CompositeWatermark = Omit<WatermarkConfig, 'imageUrl'> & { imageUrl: string | null };

export interface CompositeSubtitlePosition {
  align_items: SubtitleAlignItems;
  vertical_margin_percentage: number;
}

/** Stand-in for the clip in the admin preview — campaigns enforce the frame, not the footage. */
const ClipPlaceholder: React.FC = () => (
  <AbsoluteFill
    style={{
      background: 'linear-gradient(135deg, #1f2430, #2b3242)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <span
      style={{
        color: 'rgba(255,255,255,0.45)',
        fontFamily: 'system-ui,-apple-system,sans-serif',
        fontSize: 64,
        fontWeight: 700,
        letterSpacing: 2,
      }}
    >
      CLIPE
    </span>
  </AbsoluteFill>
);

const LayoutComposition: React.FC<{ config: PostLayoutConfig }> = ({ config }) => {
  const def = POST_LAYOUTS[config.kind as keyof typeof POST_LAYOUTS];
  if (!def) return <AbsoluteFill style={{ backgroundColor: '#000' }} />;
  const Renderer = def.Renderer;
  return (
    <Renderer
      config={config}
      media={<ClipPlaceholder />}
      renderClip={() => <ClipPlaceholder />}
    />
  );
};

export interface CampaignCompositePreviewProps {
  /** Enforced post layout (flat config). Falls back to the sample video when null. */
  postLayout?: PostLayoutConfig | null;
  /** Campaign watermark overlay; hidden when null or without an image. */
  watermark?: CompositeWatermark | null;
  /** Subtitle anchor + margin; the mock subtitle line is hidden when null. */
  subtitlePosition?: CompositeSubtitlePosition | null;
  label?: string;
  testId?: string;
  /** Frame width in px (9:16, so height = width × 16/9). */
  width?: number;
}

/**
 * Composite 9:16 preview of everything a campaign enforces: post layout
 * (or sample footage), watermark overlay and subtitle line — layered exactly
 * like the real render (overlays and subtitles above the layout, full-frame).
 */
const CampaignCompositePreview: React.FC<CampaignCompositePreviewProps> = ({
  postLayout,
  watermark,
  subtitlePosition,
  label,
  testId = 'campaign-composite-preview',
  width = 180,
}) => {
  const height = Math.round((width * 16) / 9);
  const layoutInputProps = useMemo(
    () => (postLayout ? { config: postLayout } : null),
    [postLayout],
  );

  const subtitleOffsetPx = subtitlePosition
    ? (subtitleVerticalTransformPercent(
        subtitlePosition.align_items,
        subtitlePosition.vertical_margin_percentage,
      ) /
        100) *
      height
    : 0;

  return (
    <div className="space-y-1 shrink-0 self-start">
      {label && <div className="text-xs text-muted-foreground">{label}</div>}
      <div
        className="relative rounded-md overflow-hidden bg-black flex justify-center border border-border"
        data-testid={testId}
        style={{
          width,
          height,
          ...(subtitlePosition ? { alignItems: subtitlePosition.align_items } : {}),
        }}
      >
        {/* Background: enforced layout chrome, or sample footage when none. */}
        {postLayout && layoutInputProps ? (
          <div className="absolute inset-0">
            <Player
              component={LayoutComposition as React.FC<Record<string, unknown>>}
              inputProps={layoutInputProps}
              durationInFrames={300}
              compositionWidth={DESIGN_WIDTH}
              compositionHeight={DESIGN_HEIGHT}
              fps={30}
              autoPlay
              loop
              controls={false}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        ) : (
          <video
            src="/videos/sample-video-vertical.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Campaign watermark — rendered above the layout, like the real overlay. */}
        {watermark?.imageUrl && (
          <img
            src={watermark.imageUrl}
            alt=""
            data-testid="preview-watermark"
            style={{
              position: 'absolute',
              height: `${watermark.sizePercent}%`,
              width: 'auto',
              objectFit: 'contain',
              opacity: watermark.opacityPercent / 100,
              zIndex: 20,
              ...getAnchorStyle(watermark.anchor, watermark.marginXPercent, watermark.marginYPercent),
            }}
          />
        )}

        {/* Mock subtitle line — same anchor + translateY math as the renderer. */}
        {subtitlePosition && (
          <span
            data-testid="subtitle-preview-line"
            className="relative z-10 px-1 text-center font-extrabold uppercase text-white leading-tight"
            style={{
              transform: `translateY(${subtitleOffsetPx}px)`,
              fontSize: Math.round(width * 0.061),
              textShadow: '0 0 2px #000, 0 0 4px #000, 0 0 6px #000',
            }}
          >
            Lorem ipsum
            <br />
            dolor sit amet
          </span>
        )}
      </div>
    </div>
  );
};

export default CampaignCompositePreview;
